import { App } from '@capacitor/app';
import { Capacitor, type PluginListenerHandle } from '@capacitor/core';

const AUTH_ORIGIN = 'https://conference.rubikoo.com';
const AUTH_PATHS = new Set(['/verify-email', '/reset-password', '/activate-account']);

/** Only trusted email actions, not a general-purpose external URL router. */
export function parseNativeAuthLink(value: string): string | null {
  try {
    const url = new URL(value);
    if (url.origin !== AUTH_ORIGIN || url.username || url.password || url.hash) return null;
    if (!AUTH_PATHS.has(url.pathname)) return null;
    if ([...url.searchParams.keys()].some(key => key !== 'token')) return null;
    const tokens = url.searchParams.getAll('token');
    if (tokens.length !== 1 || !/^[A-Za-z0-9._~-]{1,8192}$/.test(tokens[0])) return null;
    return `${url.pathname}?${new URLSearchParams({ token: tokens[0] })}`;
  } catch {
    return null;
  }
}

/** Register before reading launch URL so a newer warm link wins a startup race.
 * Tokens stay in memory/navigation only: never log or persist the incoming URL.
 */
export function startNativeAuthLinks(onRoute: (route: string) => void): () => void {
  if (!Capacitor.isNativePlatform()) return () => {};
  let disposed = false;
  let openedWhileStarting = false;
  let handle: PluginListenerHandle | undefined;
  const accept = (url: string, warm: boolean) => {
    if (disposed) return;
    const route = parseNativeAuthLink(url);
    if (!route) return;
    if (warm) openedWhileStarting = true;
    onRoute(route);
  };

  void (async () => {
    try {
      handle = await App.addListener('appUrlOpen', event => accept(event.url, true));
    } catch { /* Browser fallback remains available if the bridge is unavailable. */ }
    if (disposed) {
      void handle?.remove().catch(() => {});
      return;
    }
    try {
      const launch = await App.getLaunchUrl();
      if (launch && !openedWhileStarting) accept(launch.url, false);
    } catch { /* Do not prevent normal sign-in when launch URL is unavailable. */ }
  })();

  return () => {
    disposed = true;
    void handle?.remove().catch(() => {});
  };
}
